/**
 * The voice flags on a person, as Hub's sidebar voice rows and the voice
 * room's tiles draw them (one builder, `flags.ts`; one look per place).
 */
import type { Snowflake } from '@neosocial/protocol';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useT } from '../i18n';
import { listFormat } from '../i18n/core';
import { memberActions } from '../mod/perms';
import { useData } from '../store/data';
import { Icon } from '../ui/kit';
import {
  condenseFlags,
  flagStateOf,
  nextRowFlagMax,
  ROW_FLAG_MAX,
  ownServerFlags,
  voiceFlags,
  type OwnModFlags,
  type SelfLift,
  type SelfToggleView,
  type VoiceFlag,
} from './flags';
import { rosterEntryOf } from './machine';
import { useVoice } from './store';
import type { RosterMember } from './types';

/** One person's flags from the live roster (while you are in voice) and the gateway. */
export function useVoiceFlags(
  userId: Snowflake,
  guildId: Snowflake | null | undefined,
): VoiceFlag[] {
  const meId = useData((d) => d.me?.id);
  const state = useData((d) => (guildId ? d.voiceStates[guildId]?.[userId] : undefined));
  const roster = useVoice((v) => rosterEntryOf(v.session, userId, meId));
  const me = userId === meId;
  const selfMute = useVoice((v) => v.session.selfMute);
  const selfDeaf = useVoice((v) => v.session.selfDeaf);
  const localMute = useVoice((v) => !!v.session.localMutes[userId]);
  return voiceFlags(
    flagStateOf({
      roster,
      state,
      // your own toggles only while you are in voice (a stale state otherwise)
      me: me && (roster || state) ? { selfMute, selfDeaf } : null,
      localMute,
    }),
  );
}

/**
 * A person's live media (screen share and camera): the live roster while you
 * are in voice (`media`), or the gateway's voice state (`self_stream`,
 * `self_video`); either one saying so is enough.
 */
export function useMediaOf(
  userId: Snowflake,
  guildId: Snowflake | null | undefined,
): { live: boolean; camera: boolean } {
  const meId = useData((d) => d.me?.id);
  const live = useVoice((v) => {
    const r = rosterEntryOf(v.session, userId, meId) as RosterMember | undefined;
    return !!r?.screen;
  });
  const camera = useVoice((v) => {
    const r = rosterEntryOf(v.session, userId, meId) as RosterMember | undefined;
    return !!r?.camera;
  });
  const stream = useData((d) => !!(guildId && d.voiceStates[guildId]?.[userId]?.self_stream));
  const video = useData((d) => !!(guildId && d.voiceStates[guildId]?.[userId]?.self_video));
  return { live: live || stream, camera: camera || video };
}

/**
 * LIVE and the camera mark beside a person's flags (§4.7): drawn as the
 * flags are (`cls` the place's flag class), the LIVE badge in `--live`.
 */
export function MediaBadges({
  live,
  camera,
  cls,
}: {
  live: boolean;
  camera: boolean;
  cls: string;
}) {
  const { t } = useT();
  if (!live && !camera) return null;
  return (
    <>
      {camera ? (
        <span
          className={`${cls} nx-vflag gray`}
          data-flag="camera"
          role="img"
          aria-label={t('stream.camLabel')}
          data-tip={t('stream.camLabel')}
        >
          <Icon name="video" />
        </span>
      ) : null}
      {live ? (
        <span
          className="nx-live-badge sm"
          data-flag="live"
          role="img"
          aria-label={t('stream.liveLabel')}
          data-tip={t('stream.liveLabel')}
        >
          {t('stream.live')}
        </span>
      ) : null}
    </>
  );
}

/**
 * Your own server mute / deafen, channel mute / deafen where you are
 * (2026-10-28), and "can't speak here", while you are in voice.
 */
export function useOwnServerFlags(): OwnModFlags {
  const meId = useData((d) => d.me?.id);
  const roster = useVoice((v) => (meId ? rosterEntryOf(v.session, meId, meId) : undefined));
  const g = useVoice((v) => v.session.guildId);
  const state = useData((d) => (g && meId ? d.voiceStates[g]?.[meId] : undefined));
  const inVoice = useVoice((v) => v.session.status !== 'idle' && v.session.status !== 'failed');
  if (!inVoice)
    return {
      serverMute: false,
      serverDeaf: false,
      channelMute: false,
      channelDeaf: false,
      noTalk: false,
    };
  return ownServerFlags(roster, state);
}

/**
 * Which of a moderator's server mute and deafen on you, you can undo
 * yourself (`SelfLift`): what your own menus offer (`memberActions` on
 * yourself: Mute / Deafen Members in your channel, as the server checks).
 * In voice that is also what lifts a channel mute / deafen there.
 */
export function useSelfLift(): SelfLift {
  const g = useVoice((v) => v.session.guildId);
  // one look per store change, as a number (a stable value for the selector)
  const bits = useData((d) => {
    if (!g || !d.me) return 0;
    const a = memberActions(d, g, d.me.id);
    return (a.mute ? 1 : 0) | (a.deafen ? 2 : 0);
  });
  return { mute: (bits & 1) !== 0, deaf: (bits & 2) !== 0 };
}

/**
 * The flags as icons. `cls` is the place's own class (`vc-f` in the sidebar
 * and the profile card, `hubv-tf` on tiles; the member list shows none since
 * 2026-10-29); the place puts them in a box whose gap spaces them (`.vc-fs`,
 * `.ns-prof-vf`, the tile's name pill). Each icon also gets its tone (`red`: a moderator's, server or
 * channel; `gray`: the person's own, or yours; `amber`: can't speak here),
 * `data-flag`, `data-by` (server / channel), a tooltip and an accessible
 * name of its own.
 *
 * `max` (a narrow row: the sidebar): more flags than that
 * condense to the first ones and a "+N" chip (`condenseFlags`) whose name
 * and tooltip list the rest, and whose `data-flags` names them.
 */
export function VoiceFlagIcons({
  flags,
  cls,
  max,
}: {
  flags: VoiceFlag[];
  cls: string;
  max?: number;
}) {
  const tr = useT();
  const { t } = tr;
  const { shown, rest, tone } =
    max === undefined ? { shown: flags, rest: [], tone: null } : condenseFlags(flags, max);
  const restWords = rest.length ? listFormat(rest.map((f) => t(f.label))) : '';
  return (
    <>
      {shown.map((f) => (
        <span
          key={f.kind}
          className={`${cls} nx-vflag ${f.tone}`}
          data-flag={f.kind}
          data-by={f.server ? 'server' : f.channel ? 'channel' : undefined}
          role="img"
          aria-label={t(f.label)}
          data-tip={t(f.label)}
        >
          <Icon name={f.icon} />
        </span>
      ))}
      {rest.length ? (
        <span
          className={`${cls} nx-vflag nx-vflag-more ${tone ?? 'gray'}`}
          data-flags={rest.map((f) => f.kind).join(' ')}
          role="img"
          aria-label={t('um.voice.flag.more', { n: tr.fmt.num(rest.length), list: restWords })}
          data-tip={restWords}
        >
          <bdi className="nx-num">+{tr.fmt.num(rest.length)}</bdi>
        </span>
      ) : null}
    </>
  );
}

/**
 * A narrow row's flags in their own box (`cls`: the sidebar voice row's
 * `vc-fs`): at most `ROW_FLAG_MAX` icons, and
 * fewer when the row has less room, the rest folded into the "+N" chip.
 * The box may shrink (clipped) before the name goes below its floor; once
 * it clips, the row shows one item fewer until nothing is cut off
 * (`nextRowFlagMax`), so no flag ever hides past the row's edge unsaid.
 * QA 2026-10-28: with "Every role" icons (3 + "+2") the sidebar row cut
 * Bahar's third flag (her own gray mute) off, with nothing saying so.
 *
 * `reset` names what else takes the row's room (the role icon slot): when
 * it or the flags change, or the row's width does, it measures again from
 * `ROW_FLAG_MAX`.
 */
export function RowFlags({
  flags,
  cls,
  reset = '',
}: {
  flags: VoiceFlag[];
  cls: string;
  reset?: string;
}) {
  const box = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);
  const key = `${flags.map((f) => f.kind).join(' ')}|${reset}|${width}`;
  const [fit, setFit] = useState({ key, max: ROW_FLAG_MAX });
  const max = fit.key === key ? fit.max : ROW_FLAG_MAX;
  const count = flags.length;
  const stale = fit.key !== key;
  // measured only when what it draws or its room changed (not on every
  // speaking ring), before paint: a step at a time until nothing is cut off
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const clipped = el.scrollWidth > el.clientWidth + 0.5;
    const next = clipped ? nextRowFlagMax(count, max) : max;
    if (next !== max || stale) setFit({ key, max: next });
  }, [key, max, count, stale]);
  // the row's width (a window or sidebar resize, a font change): measure again
  useEffect(() => {
    const row = box.current?.parentElement;
    if (!row || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWidth(Math.round(row.clientWidth)));
    ro.observe(row);
    return () => ro.disconnect();
  }, []);
  return (
    <span className={cls} ref={box} data-flag-max={max}>
      <VoiceFlagIcons flags={flags} cls="vc-f" max={max} />
    </span>
  );
}

/**
 * Your own microphone or sound button (`micToggleView` / `soundToggleView`):
 * your toggle's state, with a moderator's or the channel's block as a badge
 * on it. `cls` is the place's button class (`nx-dbtn`, `hubv-cb`);
 * `data` carries the place's markers (`data-server-muted`, ...). `onMenu`:
 * a right-click or long-press opens the place's quick menu on the button
 * (Discord's input and output menus on mute and deafen).
 */
export function SelfToggleButton({
  view,
  cls,
  onClick,
  onMenu,
  tipPos,
  data,
}: {
  view: SelfToggleView;
  cls: string;
  onClick: () => void;
  onMenu?: (button: HTMLElement) => void;
  tipPos?: 'top' | 'bottom';
  data?: Record<`data-${string}`, string | boolean | undefined>;
}) {
  const { t } = useT();
  const name = view.blockKey
    ? t('um.voice.btnBlocked', { name: t(view.name), why: t(view.blockKey) })
    : t(view.name);
  return (
    <button
      type="button"
      className={
        cls +
        (view.pressed ? ' off' : '') +
        // a moderator's block turns the button red (server or channel)
        (view.block === 'server' || view.block === 'channel' ? ' modblock' : '') +
        (view.block === 'server' ? ' srv' : view.block === 'channel' ? ' chb' : '')
      }
      aria-pressed={view.pressed}
      aria-label={name}
      data-tip={t(view.tip)}
      data-tip-pos={tipPos}
      data-block={view.block ?? undefined}
      {...data}
      onClick={onClick}
      onContextMenu={
        onMenu
          ? (e) => {
              e.preventDefault();
              onMenu(e.currentTarget);
            }
          : undefined
      }
    >
      <Icon name={view.icon} />
      {view.block ? (
        <span className="ns-tbadge" data-tone={view.block} aria-hidden="true">
          <Icon
            name={view.block === 'server' ? 'shield' : view.block === 'channel' ? 'volume' : 'hand'}
          />
        </span>
      ) : null}
    </button>
  );
}
